import { ref } from 'vue'

const LEGACY_SYSTEM_UPDATE_NOTICE_KEY = 'escribonauta_system_update_notice_v1_dismissed'
const VERSION_CHECK_INTERVAL_MS = 60_000

type VersionPayload = {
  version?: string
}

const updateAvailable = ref(false)

let versionCheckInFlight = false
let recheckRequested = false
let reloadArmed = false
let updateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | null = null

const watchedInstallingWorkers = new WeakSet<ServiceWorker>()
const watchedRegistrations = new WeakSet<ServiceWorkerRegistration>()

const BUILT_ASSET_LOAD_ERROR_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
]

function getLocalAppVersion(): string {
  return import.meta.env.VITE_APP_VERSION || 'dev'
}

function clearLegacySystemUpdateNoticeKey(): void {
  try {
    localStorage.removeItem(LEGACY_SYSTEM_UPDATE_NOTICE_KEY)
  } catch {
    // Ignore storage errors
  }
}

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })

    if (!response.ok) return null

    const payload = (await response.json()) as VersionPayload
    if (!payload?.version || typeof payload.version !== 'string') return null

    return payload.version
  } catch {
    return null
  }
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null

  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null
  } catch {
    return null
  }
}

function watchInstallingWorker(worker: ServiceWorker | null): void {
  if (!worker || watchedInstallingWorkers.has(worker)) return

  watchedInstallingWorkers.add(worker)
  worker.addEventListener('statechange', () => {
    // "installed" is also the waiting state: the worker does not emit another
    // change when it moves from installing to waiting.
    if (worker.state !== 'installed' && worker.state !== 'activated') return

    window.setTimeout(() => {
      void checkForUpdate()
    }, 0)
  })
}

function watchRegistration(registration: ServiceWorkerRegistration): void {
  if (watchedRegistrations.has(registration)) return

  watchedRegistrations.add(registration)
  registration.addEventListener('updatefound', () => {
    watchInstallingWorker(registration.installing)
  })
  watchInstallingWorker(registration.installing)
  watchInstallingWorker(registration.waiting)
}

/**
 * The new worker finished installing and is waiting. The open page is still
 * controlled by the previous worker, so its chunks keep resolving.
 */
export function markUpdateAvailable(): void {
  if (reloadArmed) return

  updateAvailable.value = true
}

function reloadOnControllerChange(): void {
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload()
  }, { once: true })
}

function reloadNow(): void {
  if (reloadArmed) return

  reloadArmed = true
  reloadOnControllerChange()

  void (async () => {
    const registration = await getServiceWorkerRegistration()
    if (registration?.waiting && updateServiceWorker) {
      try {
        await updateServiceWorker(true)
        return
      } catch {
        window.location.reload()
        return
      }
    }

    window.location.reload()
  })()
}

export function setServiceWorkerUpdater(
  updater: (reloadPage?: boolean) => Promise<void>,
): void {
  updateServiceWorker = updater
}

function getErrorMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message
  if (typeof reason === 'string') return reason
  return ''
}

function isBuiltAssetLoadError(reason: unknown): boolean {
  const message = getErrorMessage(reason)
  return BUILT_ASSET_LOAD_ERROR_MESSAGES.some((fragment) => message.includes(fragment))
}

function handleBuiltAssetLoadFailure(): void {
  if (reloadArmed || updateAvailable.value) return
  if (versionCheckInFlight) {
    recheckRequested = true
    return
  }

  void checkForUpdate()
}

function handlePreloadError(event: VitePreloadErrorEvent): void {
  event.preventDefault()
  handleBuiltAssetLoadFailure()
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  if (!isBuiltAssetLoadError(event.reason)) return

  handleBuiltAssetLoadFailure()
}

async function checkForUpdate(): Promise<void> {
  if (reloadArmed) return
  if (versionCheckInFlight) {
    recheckRequested = true
    return
  }

  versionCheckInFlight = true

  try {
    const [deployedVersion, registration] = await Promise.all([
      fetchDeployedVersion(),
      getServiceWorkerRegistration(),
    ])

    if (registration) {
      watchRegistration(registration)
      try {
        await registration.update()
      } catch {
        // The next poll retries the update check.
      }
    }

    if (!deployedVersion) return

    const localVersion = getLocalAppVersion()
    if (deployedVersion === localVersion) {
      updateAvailable.value = false
      return
    }

    // A worker that is still installing must keep this page. A reload now
    // would be served by the current worker, with this same bundle.
    const pageHasServiceWorker = 'serviceWorker' in navigator
      && Boolean(navigator.serviceWorker.controller)
    if (!pageHasServiceWorker || registration?.waiting) {
      updateAvailable.value = true
    }
  } finally {
    versionCheckInFlight = false
    if (recheckRequested && !reloadArmed) {
      recheckRequested = false
      void checkForUpdate()
    } else {
      recheckRequested = false
    }
  }
}

export function useAppVersionCheck() {
  return {
    updateAvailable,
    reloadNow,
  }
}

/**
 * Polls /version.json and asks the browser to check for a new service worker.
 *
 * The reload banner is shown only once that worker is waiting (or when this
 * page has no service worker). Until then the current worker keeps serving
 * this bundle, including hashed chunks from the previous build. The button
 * sends SKIP_WAITING and reloads after controllerchange, when the new worker
 * is in control.
 */
export function startAppVersionWatcher(): void {
  clearLegacySystemUpdateNoticeKey()

  void checkForUpdate()

  window.setInterval(() => {
    void checkForUpdate()
  }, VERSION_CHECK_INTERVAL_MS)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate()
    }
  })

  window.addEventListener('vite:preloadError', handlePreloadError)
  window.addEventListener('unhandledrejection', handleUnhandledRejection)
}
